import { apiUrl, authHeaders } from './auth';

const base = `${apiUrl}/v2/reports/predefined`;

export interface PredefinedEnvelope<Row> {
  count: number;
  rows: Row[];
}

// ----- Monthly Campaign Profiles -----

export interface MonthlyCampaignProfileRow {
  id: number;
  full_name: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  city: string;
  state: string;
  status: string;
  funded_date: string | null;
  fund: string;
  medical_reason: string;
  primary_medical_issue: string;
  expenses: string;
  gender: string;
  goal: string;
  optional_photo_consent: boolean;
  picture: string | null;
  csc_email: string;
  preferred_language: string;
}

export interface MonthlyCampaignProfilesResponse extends PredefinedEnvelope<MonthlyCampaignProfileRow> {
  from_date: string | null;
  to_date: string | null;
}

export const fetchMonthlyCampaignProfiles = async (
  params: { from_date?: string; to_date?: string }
): Promise<MonthlyCampaignProfilesResponse> => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  const url = `${base}/monthly-campaign-profiles/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- Med Pros Needing Email -----

export interface MedProsNeedingEmailRow {
  campaign_id: number;
  med_pro: string;
  med_pro_id: number | null;
  status: string;
  patient_last_name: string;
  patient_first_name: string;
  patient_middle_name: string;
}

export interface MedProsNeedingEmailResponse extends PredefinedEnvelope<MedProsNeedingEmailRow> {
  campaign_status: string | null;
}

export const fetchMedProsNeedingEmail = async (
  params: { campaign_status?: string }
): Promise<MedProsNeedingEmailResponse> => {
  const qs = new URLSearchParams();
  if (params.campaign_status) qs.set('campaign_status', params.campaign_status);
  const url = `${base}/med-pros-needing-email/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- Total Funds Raised by Medical Center -----

export interface TotalFundsRaisedRow {
  patient_id: number;
  medical_center: string;
  location: string;
  patient_last_name: string;
  patient_first_name: string;
  patient_middle_name: string;
  total_funds_raised: string;
  status: string;
  status_date: string | null;
  medical_reason: string;
  primary_issue: string;
  secondary_issue: string;
}

export interface TotalFundsRaisedResponse extends PredefinedEnvelope<TotalFundsRaisedRow> {
  location: string | null;
  medical_center_ids: number[];
}

export const fetchTotalFundsRaised = async (
  params: { location?: string; medical_center?: string }
): Promise<TotalFundsRaisedResponse> => {
  const qs = new URLSearchParams();
  if (params.location) qs.set('location', params.location);
  if (params.medical_center) qs.set('medical_center', params.medical_center);
  const url = `${base}/total-funds-raised-by-med-center/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

export interface TotalFundsRaisedOptions {
  locations: { value: string; label: string }[];
  medical_centers: { id: number; name: string }[];
}

export const fetchTotalFundsRaisedOptions = async (
  location?: string
): Promise<TotalFundsRaisedOptions> => {
  const qs = new URLSearchParams({ options: '1' });
  if (location) qs.set('location', location);
  const url = `${base}/total-funds-raised-by-med-center/?${qs.toString()}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- Refund Audit Trail -----

export interface RefundAuditRow {
  refund_id: number;
  refunded_at: string | null;
  refunded_by: string;
  name: string;
  email: string;
  campaign_id: number | null;
  campaign_name: string;
  ntaf_id: string;
  lookup_id: string;
  original_amount: string;
  donation_refunded: string;
  tip_refunded: string;
  total_refunded: string;
  remaining_amount: string;
  is_full_refund: boolean;
  payment_method: string;
  gift_date: string | null;
  stripe_charge_id: string;
  stripe_refund_id: string;
  payout_id: string;
  status: string;
  failure_reason: string;
  completion_status: string;
  completed_at: string | null;
  completed_by: string;
  /** Who last flipped the completion flag, either direction — read from the
   *  audit log, so it survives a reopen (which clears completed_by). */
  completion_changed_by: string;
  completion_changed_at: string | null;
}

export interface RefundAuditTotals {
  count: number;
  donation_refunded: string;
  tip_refunded: string;
  total_refunded: string;
}

export interface RefundAuditResponse extends PredefinedEnvelope<RefundAuditRow> {
  from_date: string | null;
  to_date: string | null;
  status: string | null;
  completion_status: string | null;
  totals: RefundAuditTotals;
}

export const fetchRefundAudit = async (
  params: {
    from_date?: string;
    to_date?: string;
    status?: string;
    completion_status?: string;
  }
): Promise<RefundAuditResponse> => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  if (params.status) qs.set('status', params.status);
  if (params.completion_status) qs.set('completion_status', params.completion_status);
  const url = `${base}/refund-audit/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- CSV download helper -----

// ----- Campaign Request Timeline -----

export interface CampaignRequestTimelineRow {
  campaign_request_id: number;
  campaign_id: number;
  ntaf_id: string;
  client_name: string;
  csc_name: string;
  request_created: string | null;
  request_approved: string | null;
  application_completed: string | null;
  campaign_open: string | null;
  days_to_approval: number | null;
  days_approval_to_application: number | null;
  days_application_to_open: number | null;
  days_total: number | null;
}

export interface CampaignRequestTimelineResponse
  extends PredefinedEnvelope<CampaignRequestTimelineRow> {
  from_date: string | null;
  to_date: string | null;
  csc: string | null;
}

export const fetchCampaignRequestTimeline = async (
  params: { from_date?: string; to_date?: string; csc?: string }
): Promise<CampaignRequestTimelineResponse> => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  if (params.csc) qs.set('csc', params.csc);
  const url = `${base}/campaign-request-timeline/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- Campaign Open to Disbursable -----

export interface CampaignOpenToDisbursableRow {
  campaign_id: number;
  ntaf_id: string;
  client_name: string;
  csc_name: string;
  status: string;
  campaign_open: string | null;
  first_donation: string | null;
  disbursable: string | null;
  days_open_to_first_donation: number | null;
  days_open_to_disbursable: number | null;
}

export interface CampaignOpenToDisbursableResponse
  extends PredefinedEnvelope<CampaignOpenToDisbursableRow> {
  from_date: string | null;
  to_date: string | null;
  csc: string | null;
}

export const fetchCampaignOpenToDisbursable = async (
  params: { from_date?: string; to_date?: string; csc?: string }
): Promise<CampaignOpenToDisbursableResponse> => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  if (params.csc) qs.set('csc', params.csc);
  const url = `${base}/campaign-open-to-disbursable/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

// ----- Campaign Last Activity -----

export interface CampaignLastActivityRow {
  campaign_id: number;
  ntaf_id: string;
  campaign_name: string;
  status: string;
  csc_name: string;
  client_email: string;
  primary_contact_email: string;
  last_activity: string | null;
  last_activity_type: string;
  days_since_activity: number | null;
  last_donation: string | null;
  last_update: string | null;
  last_guestbook: string | null;
  last_message: string | null;
  last_fund_request: string | null;
}

export interface CampaignLastActivityResponse
  extends PredefinedEnvelope<CampaignLastActivityRow> {
  inactive_since: string | null;
  campaign_status: string | null;
  csc: string | null;
}

export const fetchCampaignLastActivity = async (
  params: { inactive_since?: string; campaign_status?: string; csc?: string }
): Promise<CampaignLastActivityResponse> => {
  const qs = new URLSearchParams();
  if (params.inactive_since) qs.set('inactive_since', params.inactive_since);
  if (params.campaign_status) qs.set('campaign_status', params.campaign_status);
  if (params.csc) qs.set('csc', params.csc);
  const url = `${base}/campaign-last-activity/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

export const downloadCsv = async (slug:
  | 'monthly-campaign-profiles'
  | 'med-pros-needing-email'
  | 'total-funds-raised-by-med-center'
  | 'monthly-cr-stats'
  | 'campaign-request-timeline'
  | 'campaign-open-to-disbursable'
  | 'campaign-last-activity'
  | 'refund-audit',
  filterParams: URLSearchParams
) => {
  const qs = new URLSearchParams(filterParams);
  qs.set('export', 'csv');
  const url = `${base}/${slug}/?${qs.toString()}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  const blob = await resp.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// ----- Monthly CR Stats -----

export interface MonthlyCRStatsNode {
  label: string;
  count: number;
  children?: MonthlyCRStatsNode[];
}

export interface MonthlyCRStatsResponse {
  from_date: string;
  to_date: string;
  net_actionable_added: number;
  sections: MonthlyCRStatsNode[];
}

export const fetchMonthlyCRStats = async (
  params: { from_date?: string; to_date?: string }
): Promise<MonthlyCRStatsResponse> => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  const url = `${base}/monthly-cr-stats/${qs.toString() ? '?' + qs.toString() : ''}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};
